import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useFriends, useFriendRequests, useSendFriendRequest, useAcceptRequest, useRejectRequest, useRemoveFriend, useUpdateLocationSharing } from "@/hooks/use-friends";
import { useHoneyBalance } from "@/hooks/use-honey";
import { timeAgo } from "@/lib/time-ago";
import { getCapacityColour } from "@/lib/capacity-utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { User, ChevronDown, ChevronUp, MapPin, Music, Camera, Video, X, Eye, Radio } from "lucide-react";
import type { Venue } from "@shared/schema";

interface ProfilePageProps {
  onClose: () => void;
  onVenueSelect: (venue: Venue) => void;
  defaultTab?: string;
}

type ProfileTab = "favourites" | "vibes" | "friends";

type VibeItem = {
  id: number;
  type: "report" | "media";
  venueId: number;
  venueName?: string;
  vibe?: string;
  capacityLevel?: number;
  mediaType?: "photo" | "video";
  thumbnailUrl?: string | null;
  cloudinaryUrl?: string | null;
  date: string;
};

export function ProfilePage({ onClose, onVenueSelect, defaultTab }: ProfilePageProps) {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const initialTab: ProfileTab = (defaultTab === "vibes" || defaultTab === "friends") ? defaultTab : "favourites";
  const [tab, setTab] = useState<ProfileTab>(initialTab);

  useEffect(() => {
    if (defaultTab === "vibes" || defaultTab === "friends" || defaultTab === "favourites") {
      setTab(defaultTab);
    }
  }, [defaultTab]);

  const { data: honeyBalance } = useHoneyBalance();
  const { data: vibesData } = useQuery<{ vibes: VibeItem[] }>({
    queryKey: ["/api/user/vibes"],
    queryFn: () => fetch("/api/user/vibes", { credentials: "include" }).then(r => r.json()),
  });
  const vibesList = vibesData?.vibes ?? [];
  const reportsCount = vibesList.filter(v => v.type === "report").length;

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("photo", file);
      const res = await fetch("/api/user/profile-photo", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || "Upload failed");
      }
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Profile photo updated" });
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message || "Please try again", variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    const type = initialTab === "friends" ? "friends" : "profile";
    apiRequest("POST", `/api/badges/clear/${type}`)
      .then(() => queryClient.invalidateQueries({ queryKey: ["/api/badges"] }))
      .catch(() => {});
  }, [initialTab, queryClient]);

  const handleTabChange = (next: ProfileTab) => {
    setTab(next);
    const type = next === "friends" ? "friends" : "profile";
    apiRequest("POST", `/api/badges/clear/${type}`)
      .then(() => queryClient.invalidateQueries({ queryKey: ["/api/badges"] }))
      .catch(() => {});
  };

  if (!user) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Header row — avatar + username + close */}
      <div className="px-5 pt-4 pb-0 flex-shrink-0">
        <div className="flex items-center gap-3.5">
          <button
            data-testid="button-profile-photo"
            onClick={() => fileInputRef.current?.click()}
            className="relative w-12 h-12 rounded-full shrink-0 overflow-hidden border-2 border-zinc-700 group"
            disabled={uploading}
          >
            {user.profilePhotoUrl ? (
              <img
                src={user.profilePhotoUrl}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-primary flex items-center justify-center">
                <User className="w-6 h-6 text-primary-foreground" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-4 h-4 text-white" />
            </div>
            {uploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePhotoUpload}
          />
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-display font-extrabold text-white truncate" data-testid="text-profile-username">@{user.username}</h2>
            <p className="text-zinc-500 text-[12px] capitalize">{user.role} account</p>
          </div>
          <Button
            data-testid="button-close-profile"
            variant="outline"
            size="icon"
            className="rounded-full border-zinc-700 text-zinc-400 h-9 w-9"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Honey + Reports stats card */}
        <div className="mt-3.5 rounded-2xl border border-primary/30 bg-primary/10 p-4 flex items-center gap-3">
          <span className="text-[32px] leading-none">🍯</span>
          <div className="min-w-0">
            <p
              className="text-[28px] font-display font-black text-primary leading-none"
              data-testid="text-profile-honey"
            >
              {honeyBalance ?? 0}
            </p>
            <p className="text-[11px] font-bold text-primary/60 tracking-wider mt-1">HONEY</p>
          </div>
          <div className="ml-auto text-right">
            <div className="flex items-center gap-1.5 justify-end">
              <Radio className="w-3.5 h-3.5 text-zinc-500" />
              <p className="text-[11px] text-zinc-500 font-semibold uppercase tracking-wider">Reports</p>
            </div>
            <p
              className="text-[22px] font-display font-black text-white leading-none mt-1"
              data-testid="text-profile-reports"
            >
              {reportsCount}
            </p>
          </div>
        </div>

        {/* Tab bar — segmented */}
        <div className="mt-3.5 bg-zinc-900 rounded-2xl p-1 flex gap-0.5">
          {([
            ["favourites", "Saved"],
            ["vibes", "My Vibes"],
            ["friends", "Friends"],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => handleTabChange(id)}
              className={`flex-1 py-2.5 rounded-xl text-[13px] font-bold whitespace-nowrap transition-colors ${
                tab === id ? "bg-zinc-700 text-white" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-5 pt-3.5 pb-4">
        {tab === "favourites" && <FavouritesTab onVenueSelect={onVenueSelect} onClose={onClose} />}
        {tab === "vibes" && <MyVibesTab vibes={vibesList} onVenueSelect={onVenueSelect} onClose={onClose} />}
        {tab === "friends" && <FriendsTab />}
      </div>
    </div>
  );
}

function FavouritesTab({ onVenueSelect, onClose }: { onVenueSelect: (v: Venue) => void; onClose: () => void }) {
  const { data: favourites, isLoading } = useQuery<any[]>({
    queryKey: ["/api/favourites"],
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array(3).fill(0).map((_, i) => (
          <div key={i} className="flex items-center gap-3.5 p-4 rounded-2xl bg-zinc-900/50 border border-zinc-800/50">
            <Skeleton className="w-[52px] h-[52px] rounded-full bg-zinc-800" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-4 w-1/3 bg-zinc-800" />
              <Skeleton className="h-3 w-1/2 bg-zinc-800" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!favourites || favourites.length === 0) {
    return (
      <div className="text-center py-12" data-testid="empty-favourites">
        <div className="text-3xl mb-3">💛</div>
        <p className="text-zinc-200 font-bold text-base mb-1.5">No favourites yet</p>
        <p className="text-zinc-500 text-[13px]">Tap the heart on any venue to save it</p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {favourites.map((v: any) => (
        <FavouriteRow
          key={v.id}
          venue={v}
          onTap={() => { onClose(); onVenueSelect(v); }}
        />
      ))}
    </div>
  );
}

function FavouriteRow({ venue, onTap }: { venue: any; onTap: () => void }) {
  const cap = venue.capacityPercent ?? 0;
  const capColor =
    cap >= 70 ? "text-red-400" : cap >= 31 ? "text-orange-400" : "text-primary";
  const capBg =
    cap >= 70 ? "bg-red-500/20 border-red-500/30" :
    cap >= 31 ? "bg-orange-500/20 border-orange-500/30" :
    "bg-primary/20 border-primary/30";
  return (
    <button
      data-testid={`button-fav-venue-${venue.id}`}
      onClick={onTap}
      className="w-full flex items-center gap-3.5 p-3.5 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/50 transition-all cursor-pointer active:scale-[0.99] text-left"
    >
      <div className={`w-[52px] h-[52px] rounded-full flex items-center justify-center border-2 shrink-0 ${capBg}`}>
        <span className={`text-sm font-bold ${capColor}`}>{cap}%</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-bold text-white truncate mb-0.5">{venue.name}</p>
        <div className="flex items-center gap-1 text-zinc-500 text-[12px] min-w-0">
          <MapPin className="w-3 h-3 shrink-0" />
          <span className="truncate">{venue.description || venue.capacityLabel || venue.type}</span>
        </div>
      </div>
      <span className="border border-zinc-700 rounded-md px-2 py-0.5 text-[11px] text-zinc-300 capitalize shrink-0">
        {venue.type}
      </span>
    </button>
  );
}

function MyVibesTab({
  vibes, onVenueSelect, onClose,
}: { vibes: VibeItem[]; onVenueSelect: (v: Venue) => void; onClose: () => void }) {
  if (vibes.length === 0) {
    return (
      <div className="text-center py-12" data-testid="empty-my-vibes">
        <Music className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
        <p className="text-zinc-200 font-bold text-base mb-1.5">No vibes yet</p>
        <p className="text-zinc-500 text-[13px]">Report a venue or share a photo to start earning Honey</p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {vibes.map((item, idx) => {
        const cap = item.capacityLevel ?? 0;
        const capColor =
          cap >= 70 ? "text-red-400" : cap >= 31 ? "text-orange-400" : "text-primary";
        const capBg =
          cap >= 70 ? "bg-red-500/20 border-red-500/30" :
          cap >= 31 ? "bg-orange-500/20 border-orange-500/30" :
          "bg-primary/20 border-primary/30";
        return (
          <button
            key={`${item.type}-${item.id}`}
            data-testid={`vibe-item-${idx}`}
            onClick={() => { onClose(); onVenueSelect({ id: item.venueId } as Venue); }}
            className="w-full flex items-center gap-3.5 p-3.5 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/50 transition-all text-left active:scale-[0.99]"
          >
            {item.type === "report" ? (
              <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 shrink-0 ${capBg}`}>
                <span className={`text-[13px] font-bold ${capColor}`}>{cap}%</span>
              </div>
            ) : (
              <div className="w-12 h-12 rounded-xl bg-zinc-800 overflow-hidden shrink-0 relative">
                {item.thumbnailUrl ? (
                  <img src={item.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {item.mediaType === "video"
                      ? <Video className="w-5 h-5 text-zinc-500" />
                      : <Camera className="w-5 h-5 text-zinc-500" />}
                  </div>
                )}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-bold text-white truncate">{item.venueName ?? "Venue"}</p>
              <p className="text-zinc-500 text-[12px] truncate">
                {item.type === "report"
                  ? `Report · ${timeAgo(item.date)}`
                  : `${item.mediaType === "video" ? "Video" : "Photo"} · ${timeAgo(item.date)}`}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function FriendsTab() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [friendUsername, setFriendUsername] = useState("");
  const [locationSharingOpen, setLocationSharingOpen] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);

  const { data: friends, isLoading: friendsLoading } = useFriends();
  const { data: requests } = useFriendRequests();
  const sendRequest = useSendFriendRequest();
  const acceptRequest = useAcceptRequest();
  const rejectRequest = useRejectRequest();
  const removeFriend = useRemoveFriend();
  const updateLocationSharing = useUpdateLocationSharing();

  const handleSendRequest = async () => {
    if (!friendUsername.trim()) return;
    try {
      const cleaned = friendUsername.trim().replace(/^@/, "");
      if (!cleaned) return;
      await sendRequest.mutateAsync(cleaned);
      toast({ title: "Friend request sent!" });
      setFriendUsername("");
    } catch (err: any) {
      let msg = "Failed to send request";
      try {
        const text = err?.message || "";
        const jsonPart = text.includes("{") ? text.substring(text.indexOf("{")) : "";
        if (jsonPart) msg = JSON.parse(jsonPart).message || msg;
      } catch {}
      toast({ title: msg, variant: "destructive" });
    }
  };

  const friendsList = Array.isArray(friends) ? friends : [];
  const requestsList = Array.isArray(requests) ? requests : [];

  return (
    <div className="space-y-5">
      {/* Add a friend */}
      <div className="space-y-2">
        <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">Add a friend</p>
        <div className="flex gap-2">
          <Input
            data-testid="input-friend-username"
            placeholder="Username"
            value={friendUsername}
            onChange={e => setFriendUsername(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSendRequest()}
            className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 rounded-xl"
          />
          <Button
            data-testid="button-send-friend-request"
            onClick={handleSendRequest}
            disabled={sendRequest.isPending || !friendUsername.trim()}
            className="bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 rounded-xl"
          >
            {sendRequest.isPending ? "..." : "Send"}
          </Button>
        </div>
      </div>

      {/* Pending friend requests */}
      {requestsList.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">Friend requests</p>
          {requestsList.map((req: any) => (
            <div
              key={req.id}
              data-testid={`friend-request-${req.id}`}
              className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-900 border border-zinc-800"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shrink-0">
                <span className="text-[14px] font-extrabold text-white">{req.username[0]?.toUpperCase()}</span>
              </div>
              <span className="text-white text-[14px] font-bold flex-1 min-w-0 truncate">@{req.username}</span>
              <Button
                data-testid={`button-accept-${req.id}`}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 rounded-full"
                onClick={() => acceptRequest.mutate(req.id)}
                disabled={acceptRequest.isPending}
              >
                Accept
              </Button>
              <Button
                data-testid={`button-reject-${req.id}`}
                size="sm"
                variant="outline"
                className="border-zinc-700 text-zinc-400 h-8 px-3 rounded-full"
                onClick={() => rejectRequest.mutate(req.id)}
                disabled={rejectRequest.isPending}
              >
                Decline
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Friends list */}
      <div className="space-y-2">
        <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-2">
          Friends
          {friendsList.length > 0 && (
            <span className="bg-zinc-800 text-zinc-300 text-[10px] px-2 py-0.5 rounded-full">{friendsList.length}</span>
          )}
        </p>

        {friendsLoading ? (
          <div className="space-y-2">
            {Array(2).fill(0).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3.5 rounded-2xl bg-zinc-900/50 border border-zinc-800/50">
                <Skeleton className="w-11 h-11 rounded-full bg-zinc-800" />
                <Skeleton className="h-4 w-1/3 bg-zinc-800" />
              </div>
            ))}
          </div>
        ) : friendsList.length === 0 ? (
          <div className="text-center py-8" data-testid="empty-friends">
            <User className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
            <p className="text-zinc-500 text-[13px]">No friends yet — search by username above</p>
          </div>
        ) : (
          <div className="space-y-2">
            {friendsList.map((friend: any) => (
              <FriendRow
                key={friend.id}
                friend={friend}
                onViewVibes={() => setLocation(`/friends/${friend.id}/vibes`)}
                onRemoveConfirm={() => setConfirmRemoveId(friend.id)}
                onRemove={() => { removeFriend.mutate(friend.id); setConfirmRemoveId(null); }}
                onCancelRemove={() => setConfirmRemoveId(null)}
                isConfirmingRemove={confirmRemoveId === friend.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Per-friend location sharing toggles */}
      {friendsList.length > 0 && (
        <div className="space-y-2">
          <button
            data-testid="button-toggle-location-sharing"
            onClick={() => setLocationSharingOpen(!locationSharingOpen)}
            className="flex items-center gap-2 text-[11px] font-bold text-zinc-500 uppercase tracking-wider hover:text-zinc-400 transition-colors w-full"
          >
            Location sharing
            {locationSharingOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {locationSharingOpen && (
            <div className="space-y-3 p-4 rounded-2xl bg-zinc-900 border border-zinc-800">
              <p className="text-zinc-500 text-[12px]">When on, your friend sees which venue you were last spotted at</p>
              {friendsList.map((friend: any) => (
                <div key={friend.id} className="flex items-center justify-between">
                  <label className="text-zinc-300 text-[13px]">Share with @{friend.username}</label>
                  <Switch
                    data-testid={`switch-location-${friend.id}`}
                    checked={!!friend.iShareWithFriend}
                    onCheckedChange={(enabled) =>
                      updateLocationSharing.mutate({ friendId: friend.id, enabled })
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FriendRow({
  friend, onViewVibes, onRemoveConfirm, onRemove, onCancelRemove, isConfirmingRemove,
}: {
  friend: any;
  onViewVibes: () => void;
  onRemoveConfirm: () => void;
  onRemove: () => void;
  onCancelRemove: () => void;
  isConfirmingRemove: boolean;
}) {
  const hasVenue = !!friend.locationSharingEnabled && !!friend.venueName;
  const activeRecently =
    !!friend.lastSeenAt && Date.now() - new Date(friend.lastSeenAt).getTime() <= 30 * 60 * 1000;
  const isOnline = hasVenue && activeRecently;
  const ringColour =
    isOnline && friend.colourCode ? getCapacityColour(friend.colourCode) : null;
  return (
    <div
      data-testid={`friend-card-${friend.id}`}
      className="p-3.5 rounded-2xl bg-zinc-900 border border-zinc-800 space-y-3"
    >
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          {friend.profilePhotoUrl ? (
            <img
              src={friend.profilePhotoUrl}
              alt={friend.username}
              data-testid={`friend-avatar-${friend.id}`}
              className="w-11 h-11 rounded-full object-cover border-2"
              style={{ borderColor: ringColour ?? "rgb(63 63 70)" }}
            />
          ) : (
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center border-2 bg-gradient-to-br from-blue-500 to-indigo-600"
              data-testid={`friend-avatar-${friend.id}`}
              style={{ borderColor: ringColour ?? "rgb(63 63 70)" }}
            >
              <span className="text-[15px] font-extrabold text-white">{friend.username[0]?.toUpperCase()}</span>
            </div>
          )}
          <div
            className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-zinc-900 ${
              isOnline ? "bg-emerald-500" : "bg-zinc-600"
            }`}
            data-testid={`friend-online-${friend.id}`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-[14px] font-bold truncate">@{friend.username}</p>
          {hasVenue ? (
            <p
              className={`text-[12px] truncate flex items-center gap-1 ${
                isOnline ? "text-emerald-500" : "text-zinc-400"
              }`}
            >
              <MapPin className="w-3 h-3 shrink-0" />
              {isOnline ? "At" : "Last seen at"} {friend.venueName}
              {friend.lastSeenAt && <span className="text-zinc-500"> · {timeAgo(friend.lastSeenAt)}</span>}
            </p>
          ) : (
            <p className="text-zinc-500 text-[12px]">Location not shared</p>
          )}
        </div>
        {isOnline && (
          <div className="bg-emerald-500/15 border border-emerald-500/30 rounded-lg px-2.5 py-1 shrink-0">
            <span className="text-[11px] font-bold text-emerald-500">Out now</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          data-testid={`button-view-vibes-${friend.id}`}
          size="sm"
          variant="outline"
          className="border-zinc-700 text-zinc-300 h-7 text-[11px] gap-1 rounded-full"
          onClick={onViewVibes}
        >
          <Eye className="w-3 h-3" />
          View vibes
        </Button>
        {isConfirmingRemove ? (
          <div className="flex items-center gap-1 ml-auto">
            <span className="text-[11px] text-zinc-500">Remove?</span>
            <Button
              size="sm"
              variant="outline"
              className="border-red-800 text-red-400 h-7 text-[11px] px-2 rounded-full"
              onClick={onRemove}
            >
              Yes
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-zinc-700 text-zinc-400 h-7 text-[11px] px-2 rounded-full"
              onClick={onCancelRemove}
            >
              No
            </Button>
          </div>
        ) : (
          <button
            data-testid={`button-remove-friend-${friend.id}`}
            className="text-[11px] text-zinc-600 hover:text-red-400 transition-colors ml-auto"
            onClick={onRemoveConfirm}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
